/**
 * @fileoverview Endless Runner Types
 * @description Type definitions for the Endless Runner game
 */

/**
 * Game difficulty levels
 */
export type RunnerDifficulty = "easy" | "medium" | "hard";

/**
 * Game state
 */
export interface RunnerGameState {
  /** Current score (distance traveled) */
  score: number;
  /** High score for this session */
  highScore: number;
  /** Game speed multiplier */
  speed: number;
  /** Whether game is active */
  isPlaying: boolean;
  /** Whether game is paused */
  isPaused: boolean;
  /** Whether game is over */
  isGameOver: boolean;
  /** Number of jumps made */
  jumps: number;
  /** Obstacles avoided */
  obstaclesAvoided: number;
  /** Current difficulty */
  difficulty: RunnerDifficulty;
  /** Time played in seconds */
  timePlayedSeconds: number;
  /** Start time */
  startedAt: string | null;
  /** End time */
  endedAt: string | null;
}

/**
 * Game result
 */
export interface RunnerResult {
  /** Final score */
  score: number;
  /** Distance traveled */
  distance: number;
  /** Obstacles avoided */
  obstaclesAvoided: number;
  /** Time played in seconds */
  timeSeconds: number;
  /** Difficulty played */
  difficulty: RunnerDifficulty;
  /** Points earned (weekend mode) */
  pointsEarned: number;
  /** Whether this is a new high score */
  isNewHighScore: boolean;
}

/**
 * Obstacle type
 */
export interface Obstacle {
  id: string;
  /** X position (from right edge) */
  x: number;
  /** Height of obstacle */
  height: number;
  /** Width of obstacle */
  width: number;
  /** Type of obstacle */
  type: "cactus" | "rock" | "bird";
  /** Whether passed (avoided) */
  passed: boolean;
}

/**
 * Player state
 */
export interface PlayerState {
  /** Y position (0 = ground) */
  y: number;
  /** Vertical velocity */
  velocityY: number;
  /** Whether jumping */
  isJumping: boolean;
  /** Whether ducking */
  isDucking: boolean;
}

/**
 * Difficulty configuration
 */
export interface RunnerDifficultyConfig {
  difficulty: RunnerDifficulty;
  label: string;
  description: string;
  color: string;
  /** Initial game speed */
  initialSpeed: number;
  /** Speed increase rate */
  speedIncreaseRate: number;
  /** Max speed */
  maxSpeed: number;
  /** Obstacle spawn interval (ms) */
  obstacleInterval: number;
  /** Jump force */
  jumpForce: number;
  /** Gravity */
  gravity: number;
}

/**
 * Difficulty configurations
 */
export const RUNNER_DIFFICULTY_CONFIGS: Record<RunnerDifficulty, RunnerDifficultyConfig> = {
  easy: {
    difficulty: "easy",
    label: "Makkelijk",
    description: "Langzamer tempo, minder obstakels",
    color: "#22C55E",
    initialSpeed: 4,
    speedIncreaseRate: 0.001,
    maxSpeed: 8,
    obstacleInterval: 2500,
    jumpForce: 15,
    gravity: 0.8,
  },
  medium: {
    difficulty: "medium",
    label: "Normaal",
    description: "Standaard snelheid en obstakels",
    color: "#F59E0B",
    initialSpeed: 6,
    speedIncreaseRate: 0.002,
    maxSpeed: 12,
    obstacleInterval: 1800,
    jumpForce: 16,
    gravity: 0.9,
  },
  hard: {
    difficulty: "hard",
    label: "Moeilijk",
    description: "Sneller tempo, meer obstakels",
    color: "#EF4444",
    initialSpeed: 8,
    speedIncreaseRate: 0.003,
    maxSpeed: 16,
    obstacleInterval: 1200,
    jumpForce: 17,
    gravity: 1.0,
  },
};

/**
 * User stats for Endless Runner
 */
export interface RunnerUserStats {
  easy: RunnerDifficultyStats;
  medium: RunnerDifficultyStats;
  hard: RunnerDifficultyStats;
  total_games: number;
  total_score: number;
  total_time_seconds: number;
}

export interface RunnerDifficultyStats {
  games_played: number;
  best_score: number;
  /** Character used for the best score */
  best_score_character?: CharacterId;
  total_score: number;
  total_obstacles_avoided: number;
  best_time_seconds: number;
  total_time_seconds: number;
}

export const EMPTY_RUNNER_STATS: RunnerUserStats = {
  easy: {
    games_played: 0,
    best_score: 0,
    total_score: 0,
    total_obstacles_avoided: 0,
    best_time_seconds: 0,
    total_time_seconds: 0,
  },
  medium: {
    games_played: 0,
    best_score: 0,
    total_score: 0,
    total_obstacles_avoided: 0,
    best_time_seconds: 0,
    total_time_seconds: 0,
  },
  hard: {
    games_played: 0,
    best_score: 0,
    total_score: 0,
    total_obstacles_avoided: 0,
    best_time_seconds: 0,
    total_time_seconds: 0,
  },
  total_games: 0,
  total_score: 0,
  total_time_seconds: 0,
};

/**
 * Weekend config for Endless Runner
 */
export interface RunnerWeekendConfig {
  /** Points for setting a new personal high score */
  points_new_highscore: number;
  /** Points for participation (just playing) */
  points_participation: number;
  /** Max plays per day (0 = unlimited) */
  max_plays_per_day: number;
  /** Allow unlimited plays */
  allow_unlimited_plays: boolean;
}

export const DEFAULT_RUNNER_WEEKEND_CONFIG: RunnerWeekendConfig = {
  points_new_highscore: 25,
  points_participation: 5,
  max_plays_per_day: 10,
  allow_unlimited_plays: false,
};

/**
 * Character IDs
 */
export type CharacterId = "opa" | "oma";

/**
 * Character configuration
 */
export interface CharacterConfig {
  id: CharacterId;
  name: string;
  emoji: string;
  abilityName: string;
  abilityDescription: string;
  /** Jump force multiplier (1.0 = normal, 1.25 = 25% higher) */
  jumpForceMultiplier: number;
  /** Number of lives (1 = normal, 3 = can survive 2 hits) */
  lives: number;
  /** Max special jumps (Opa only, 0 = unlimited normal jumps) */
  maxSpecialJumps: number;
  /** Invincibility duration after hit in ms (Oma only) */
  invincibilityDuration: number;
}

/**
 * Character ability settings (admin configurable)
 */
export interface CharacterAbilitySettings {
  opa: {
    jumpForceMultiplier: number;
    maxSpecialJumps: number;
  };
  oma: {
    lives: number;
    invincibilityDuration: number;
  };
}

export const DEFAULT_CHARACTER_ABILITY_SETTINGS: CharacterAbilitySettings = {
  opa: {
    jumpForceMultiplier: 1.25,
    maxSpecialJumps: 3,
  },
  oma: {
    lives: 3,
    invincibilityDuration: 1500,
  },
};

/**
 * Character configurations
 */
export const CHARACTER_CONFIGS: Record<CharacterId, CharacterConfig> = {
  opa: {
    id: "opa",
    name: "Opa",
    emoji: "👴",
    abilityName: "Hoge Springer",
    abilityDescription: "Springt 25% hoger (max 3x per spel)",
    jumpForceMultiplier: 1.25,
    lives: 1,
    maxSpecialJumps: 3,
    invincibilityDuration: 0,
  },
  oma: {
    id: "oma",
    name: "Oma",
    emoji: "👵",
    abilityName: "Drie Levens",
    abilityDescription: "Kan 3 botsingen overleven",
    jumpForceMultiplier: 1.0,
    lives: 3,
    maxSpecialJumps: 0,
    invincibilityDuration: 1500,
  },
};

/**
 * Extended game state with character info
 */
export interface RunnerCharacterState {
  /** Selected character */
  characterId: CharacterId;
  /** Remaining lives */
  livesRemaining: number;
  /** Special jumps used (Opa) */
  specialJumpsUsed: number;
  /** Whether currently invincible (Oma after hit) */
  isInvincible: boolean;
  /** Invincibility end time */
  invincibilityEndTime: number | null;
}

/**
 * Extended result with character info
 */
export interface RunnerResultWithCharacter extends RunnerResult {
  /** Character used */
  characterId: CharacterId;
  /** Lives remaining (Oma) */
  livesRemaining: number;
  /** Special jumps used (Opa) */
  specialJumpsUsed: number;
}

